import React, { useState } from 'react';
import { useUsers, ROLES, canManageUser } from '../context/UsersContext';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiEdit2, FiLock, FiPause, FiSlash, FiArchive, FiRefreshCw, FiShield } from 'react-icons/fi';
import { format } from 'date-fns';
import UserFormModal from '../components/UserFormModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ConfirmActionModal from '../components/ConfirmActionModal';

const statusConfig = {
  active:   { backgroundColor: '#F0FDF4', color: '#16A34A' },
  paused:   { backgroundColor: '#FFFBEB', color: '#D97706' },
  blocked:  { backgroundColor: '#FEF2F2', color: '#EF4444' },
  archived: { backgroundColor: '#F1F5F9', color: '#64748B' },
};

const UsersPage = () => {
  const { currentUser, getVisibleUsers, addUser, updateUser, changePassword, setStatus } = useUsers();
  const [formModal, setFormModal] = useState({ open: false, user: null });
  const [pwModal, setPwModal] = useState({ open: false, user: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, user: null });
  const [guardError, setGuardError] = useState('');

  if (currentUser.role === 'sales') {
    return (
      <div className="max-w-2xl mx-auto mt-24 text-center">
        <SafeIcon icon={FiShield} className="text-5xl mx-auto mb-4" style={{ color: '#CBD5E1' }} />
        <h2 className="text-xl font-bold" style={{ color: '#64748B' }}>Access Restricted</h2>
        <p className="mt-2 text-sm" style={{ color: '#94A3B8' }}>You do not have permission to view this section.</p>
      </div>
    );
  }

  const users = getVisibleUsers();
  const openConfirm = (action, user) => { setGuardError(''); setConfirmModal({ open: true, action, user }); };

  const handleConfirm = () => {
    const { action, user } = confirmModal;
    let err = null;
    if (action === 'pause') err = setStatus(user.id, user.status === 'paused' ? 'active' : 'paused');
    if (action === 'block') err = setStatus(user.id, user.status === 'blocked' ? 'active' : 'blocked');
    if (action === 'archive') err = setStatus(user.id, 'archived');
    if (action === 'restore') err = setStatus(user.id, 'active');
    if (err) { setGuardError(err); return; }
    setConfirmModal({ open: false, action: null, user: null });
    setGuardError('');
  };

  const confirmLabels = {
    pause: u => u.status === 'paused' ? 'Unpause' : 'Pause',
    block: u => u.status === 'blocked' ? 'Unblock' : 'Block',
    archive: () => 'Archive',
    restore: () => 'Restore',
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Users</h1>
        {canManageUser(currentUser.role, 'sales') && (
          <button onClick={() => setFormModal({ open: true, user: null })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-sm transition-all"
            style={{ backgroundColor: '#2563EB', boxShadow: '0 1px 3px rgba(37,99,235,0.3)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1D4ED8'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}>
            <SafeIcon icon={FiPlus} /> Add User
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              <tr>
                {['Full Name', 'Email', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} className="p-4 text-[10px] font-black uppercase tracking-widest" style={{ color: '#94A3B8' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="6" className="p-12 text-center text-sm italic" style={{ color: '#94A3B8' }}>No users found.</td></tr>
              ) : users.map(user => {
                const isSelf = currentUser.id === user.id;
                const canManage = canManageUser(currentUser.role, user.role) && !isSelf;
                return (
                  <tr key={user.id} className="transition-colors" style={{ borderBottom: '1px solid #F1F5F9' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm" style={{ color: '#0F172A' }}>{user.name}</p>
                        {isSelf && (
                          <span className="text-[10px] font-black px-1.5 py-0.5 rounded uppercase"
                            style={{ backgroundColor: '#EFF6FF', color: '#2563EB' }}>You</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-sm" style={{ color: '#64748B' }}>{user.email}</td>
                    <td className="p-4">
                      <span className="text-xs font-black uppercase"
                        style={{ color: user.role === 'superadmin' ? '#7C3AED' : '#64748B' }}>
                        {ROLES[user.role]?.label || user.role}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="px-2.5 py-1 text-[10px] font-bold uppercase rounded-full"
                        style={statusConfig[user.status] || statusConfig.archived}>
                        {user.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm" style={{ color: '#64748B' }}>{format(new Date(user.createdAt), 'MMM d, yyyy')}</td>
                    <td className="p-4">
                      {canManage ? (
                        <UserActions user={user}
                          onEdit={() => setFormModal({ open: true, user })}
                          onPassword={() => setPwModal({ open: true, user })}
                          onPause={() => openConfirm('pause', user)}
                          onBlock={() => openConfirm('block', user)}
                          onArchive={() => openConfirm('archive', user)}
                          onRestore={() => openConfirm('restore', user)} />
                      ) : <span className="text-xs italic" style={{ color: '#CBD5E1' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <UserFormModal isOpen={formModal.open} user={formModal.user} currentUser={currentUser}
        onClose={() => setFormModal({ open: false, user: null })}
        onSubmit={data => { formModal.user ? updateUser(formModal.user.id, data) : addUser(data); setFormModal({ open: false, user: null }); }} />
      <ChangePasswordModal isOpen={pwModal.open} user={pwModal.user}
        onClose={() => setPwModal({ open: false, user: null })}
        onSubmit={pw => { changePassword(pwModal.user.id, pw); setPwModal({ open: false, user: null }); }} />
      <ConfirmActionModal isOpen={confirmModal.open}
        label={confirmModal.action && confirmModal.user ? confirmLabels[confirmModal.action](confirmModal.user) : ''}
        userName={confirmModal.user?.name}
        onClose={() => { setConfirmModal({ open: false, action: null, user: null }); setGuardError(''); }}
        onConfirm={handleConfirm} errorMessage={guardError} />
    </div>
  );
};

const UserActions = ({ user, onEdit, onPassword, onPause, onBlock, onArchive, onRestore }) => (
  <div className="flex items-center gap-1 flex-wrap">
    <ActionBtn icon={FiEdit2} label="Edit" onClick={onEdit} hoverBg="#EFF6FF" hoverColor="#2563EB" baseColor="#94A3B8" />
    <ActionBtn icon={FiLock} label="Password" onClick={onPassword} hoverBg="#F8FAFC" hoverColor="#334155" baseColor="#94A3B8" />
    {user.status !== 'archived' && (
      <>
        <ActionBtn icon={FiPause} label={user.status === 'paused' ? 'Unpause' : 'Pause'} onClick={onPause} hoverBg="#FFFBEB" hoverColor="#D97706" baseColor="#94A3B8" />
        <ActionBtn icon={FiSlash} label={user.status === 'blocked' ? 'Unblock' : 'Block'} onClick={onBlock} hoverBg="#FEF2F2" hoverColor="#EF4444" baseColor="#94A3B8" />
        <ActionBtn icon={FiArchive} label="Archive" onClick={onArchive} hoverBg="#F8FAFC" hoverColor="#334155" baseColor="#94A3B8" />
      </>
    )}
    {user.status === 'archived' && (
      <ActionBtn icon={FiRefreshCw} label="Restore" onClick={onRestore} hoverBg="#F0FDF4" hoverColor="#16A34A" baseColor="#94A3B8" />
    )}
  </div>
);

const ActionBtn = ({ icon, label, onClick, hoverBg, hoverColor, baseColor }) => (
  <button onClick={onClick} title={label}
    className="p-1.5 rounded transition-all"
    style={{ color: baseColor }}
    onMouseEnter={e => { e.currentTarget.style.backgroundColor = hoverBg; e.currentTarget.style.color = hoverColor; }}
    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = baseColor; }}>
    <SafeIcon icon={icon} className="text-sm" />
  </button>
);

export default UsersPage;